import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "./utils";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface JobPositionOption {
  value: string;
  label: string;
  status: "Vacant" | "Filled";
}

interface JobPositionSelectProps {
  options: JobPositionOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function JobPositionSelect({
  options,
  selected,
  onChange,
  placeholder = "Select positions...",
  className,
  disabled = false,
}: JobPositionSelectProps) {
  const [open, setOpen] = React.useState(false);

  const vacantOptions = options.filter((o) => o.status === "Vacant");
  const occupiedOptions = options.filter((o) => o.status === "Filled");

  const handleToggle = (value: string, isVacant: boolean) => {
    if (!isVacant) return;
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleRemove = (value: string) => {
    onChange(selected.filter((v) => v !== value));
  };

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full min-h-10 items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed bg-gray-50",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selected.length === 0 && (
              <span className="text-gray-400">{placeholder}</span>
            )}
            {selectedOptions.map((opt) => (
              <Badge
                key={opt.value}
                variant="secondary"
                className="truncate max-w-[200px] px-2 py-0.5 text-xs font-normal bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md"
              >
                <span className="truncate">{opt.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(opt.value);
                  }}
                  className="ml-1 text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              </Badge>
            ))}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400 ml-2" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white rounded-xl border border-gray-200" align="start">
        <Command
          className="bg-transparent"
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder="Search positions..."
            className="border-none focus:ring-0 px-4 py-2.5 text-sm focus:outline-none"
          />
          <CommandList className="max-h-80" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty className="py-3 text-sm text-gray-500 text-center">No positions found.</CommandEmpty>

            {/* Vacant Section */}
            {vacantOptions.length > 0 && (
              <CommandGroup
                heading={<span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Vacant</span>}
                className="px-1"
              >
                {vacantOptions.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleToggle(option.value, true)}
                      className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50 aria-selected:bg-gray-50 rounded-lg mx-1"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {/* Dark square icon */}
                        <div className="w-4 h-4 rounded-sm bg-gray-700 shrink-0" />
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0 transition-opacity",
                            isSelected ? "opacity-100 text-gray-900" : "opacity-0"
                          )}
                        />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {option.label}
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className="rounded-md border-0 font-bold px-2 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 shrink-0"
                      >
                        Vacant
                      </Badge>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* Occupied Section */}
            {occupiedOptions.length > 0 && (
              <CommandGroup
                heading={<span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Occupied</span>}
                className="px-1"
              >
                {occupiedOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    disabled
                    className="flex items-center justify-between px-3 py-2.5 opacity-50 cursor-not-allowed rounded-lg mx-1"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {/* Light grey square icon */}
                      <div className="w-4 h-4 rounded-sm bg-gray-300 shrink-0" />
                      <div className="w-4 shrink-0" />
                      <span className="text-sm font-medium text-gray-500 truncate">
                        {option.label}
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="rounded-md border-0 font-bold px-2 py-0.5 text-[10px] bg-gray-100 text-gray-500 shrink-0"
                    >
                      Occupied
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>

          {/* Footer */}
          {vacantOptions.length === 0 && occupiedOptions.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <p className="text-xs font-medium text-amber-600">
                Please select at least one vacant position
              </p>
            </div>
          )}
          {vacantOptions.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <p className="text-xs font-medium text-amber-600">
                Please select at least one vacant position
              </p>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
